import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { FixtureOverviewBezel } from '@sorare/core/src/atoms/ui/bezels/FixtureOverviewBezel';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { getRewardRGBColor } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';

import type { AlternativeRewardButton_anyRewardConfigInterface } from './__generated__/index.graphql';

const NoPaddingFixtureOverviewBezel = styled(FixtureOverviewBezel)`
  padding: 0;
`;
const BezelContent = styled(Horizontal).attrs({ center: true, gap: 2 })`
  padding: var(--unit) 0;
  width: 130px;
`;

type Props = {
  anyRewardConfigs: AlternativeRewardButton_anyRewardConfigInterface[];
  onClick: () => void;
};
export const AlternativeRewardButton = ({
  anyRewardConfigs,
  onClick,
}: Props) => {
  const firstAnyRewardConfig = anyRewardConfigs.at(0);
  if (!firstAnyRewardConfig) {
    return null;
  }

  const rgbColor = getRewardRGBColor(firstAnyRewardConfig);

  return (
    <ButtonBase onClick={onClick}>
      <NoPaddingFixtureOverviewBezel rgbColor={rgbColor}>
        <BezelContent>
          {anyRewardConfigs.map(anyRewardConfig => {
            return (
              <Horizontal key={anyRewardConfig.id}>
                <AnyRewardConfig
                  variant={Variant.TEXT}
                  anyRewardConfig={anyRewardConfig}
                />
                {isType(anyRewardConfig, 'CardShardRewardConfig') &&
                  !!anyRewardConfig.flavour?.iconUrl && (
                    <img
                      height={24}
                      src={anyRewardConfig.flavour.iconUrl}
                      alt={anyRewardConfig.flavour.displayName}
                    />
                  )}
              </Horizontal>
            );
          })}
        </BezelContent>
      </NoPaddingFixtureOverviewBezel>
    </ButtonBase>
  );
};

AlternativeRewardButton.fragments = {
  anyRewardConfigInterface: gql`
    fragment AlternativeRewardButton_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ... on CardShardRewardConfig {
        id
        flavour {
          slug
          iconUrl
          displayName
        }
      }
      ...AnyRewardConfig_anyRewardConfigInterface
      ...getRewardRGBColor_anyRewardConfigInterface
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${getRewardRGBColor.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<AlternativeRewardButton_anyRewardConfigInterface>,
};
